<template>
  <tile class="opc-zobundle">
    <foldable v-model="isExpanded" :title="title">
      <Header slot="head" :logo="logo" :title="title" :ready="!licenseErrTips">
        <div class="opc-zm-base-card__view-detail-button">
          <zm-button class="opc-zm-base-card__link" type="link" @click="handlePlanDetailsClick" v-if="isExpanded && planDetailLabel">
            {{ planDetailLabel }}
          </zm-button>
        </div>
        <div
          class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset"
          v-if="!isExpanded"
          v-html="plansSummary"
        ></div>
      </Header>
      <div class="opc-zobundle__main">
        <h2 class="opc-foldable__heading">{{ $t('billing.zone.select_license') }}</h2>
        <p class="opc-foldable__text">
          {{ $t('billing.zobizplus.unlimited_desc') }}
          <span v-if='!migratePlanState.is.upgrade && !unlockBizLicenseCount'> {{ $t('billing.zobizplus.unlimited_license_desc') }}</span>
          <zm-button v-if="planDetailLabel" type="link" @click="handleExploreOtherPlansClick">
            {{ $t('billing.zone.explore_other_plans') }}
          </zm-button>
        </p>
        <div class="opc-foldable__space"></div>
        <Alert v-if="licenseErrTips" type="warning" :closable="false">
          <p v-html="licenseErrTips"></p>
        </Alert>
        <number-select
          ref="regionsSelect"
          :label="$t('billing.zobizplus.UC')"
          unit=""
          :optionList="callingPlanOptions"
          v-model="callingPlanRegions"
          :title="$t('billing.pbx.calling_plans')"
          :deletable="callingPlanRegions.length > 1"
          :selectTitle="$t('billing.pbx.calling_regions')"
          :inputTitle="$t('billing.pbx.user_number')"
        />
        <zm-button type="link" v-if="showAddAnotherRegionCTA" @click="addAnotherRegion">
          {{ $t('billing.zobizplus.add_another') }}
        </zm-button>
        <!-- banner -->
        <zm-alert class="reason-tips" v-if="licenseTips" type="info" :closable="false">
          <i class="zm-icon-info" style="color: #9191A4;"></i>
          <p v-html="licenseTips"></p>
        </zm-alert>
        <div class="opc-foldable__space2x"></div>
        <cascade-cycle v-if="showCycle" :config="cascadeCycleConfig" v-model="cycleAndUsageFee">
          <div slot="group1-title">
            <span class="opc-foldable__heading" id="group1-title">{{ $t('billing.pbx.calling_fee_label.metered') }}</span>
            <zm-tooltip placement="top" tab-to-leave>
              <p v-html="$t('billing.pbx.calling_fee_tips', [$t('billing.pbx.regions.US')])" slot="content"></p>
              <zm-button :aria-label="'Learn More about '" type="text" ghost size="mini" style="top: -2px">
                <i class="zm-icon-info-outline" />
              </zm-button>
            </zm-tooltip>
          </div>
        </cascade-cycle>
        <appeal-cycle v-if="planBillInfo" :cycle="i18n_cycle(planBillInfo.billing_cycle)" :payment="planBillInfo.payment" />
      </div>
    </foldable>
  </tile>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { groupBy, get } from 'lodash';
import Tile from '@/components/tile';
import Foldable from '@/components/foldable';
import { planTypes, BILLING_CYCLE, limitBillingCycleWithBaseZoneTypes, BillingCycleEnum, unlockBizLicenseCount, BASE_PLAN_SUB_TYPES, maxBizAndBizPlusLicenseCount } from '@/common/constants';
import NumberSelect from '../component/select-number-list.vue';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import FullComparisonDialog from '@/plans/ZOOM_MEETING/dialog/full-comparison-dialog';
import PlanDetails from '@/plans/ZOOM_MEETING/dialog/plan-details';
import CascadeCycle from '@/plans/ZOOM_PHONE/component/opc-card-cascade-next.vue';
import { PBX_REGIONS, regions } from '@/plans/ZOOM_PHONE/helper/constants';
import { computedPbxBasePlans, computedPbxPlans } from '@/common/service/computers/plans/pbx';
import Header from '../header.vue';
import { checkBizPlusLicense, costMap } from '../utils';
import { plansof } from '@/utils/chain';
import AppealCycle from '@/plans/ZOOM_PHONE/component/appeal-cycle.vue';
import { i18n_cycle } from '@/utils/i18n-shortcuts';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import { ONLINE_NOT_SUPPORT_BILLING_CYCLE } from "@/common/service/non-standard-billing-cycle-utils";

export default {
  name: 'BASE',
  components: {
    Tile,
    Foldable,
    Header,
    NumberSelect,
    CascadeCycle,
    AppealCycle,
    Alert
  },
  data() {
    return {
      isExpanded: true,
      logo: require('@/assets/plans/icon_ZM.svg'),
      title: this.$t('billing.planform.title.zoom_one_biz_plus'),
      callingPlanRegions: [],
      ready: false,
      fullComparisonDialog: new ServiceConfirm(FullComparisonDialog, this.$store),
      planDetailsDialog: new ServiceConfirm(PlanDetails, this.$store),
      licenseErrTips: "",
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'plancodeMapSubscriptionDisabled',
      'fromCampaignLink',
      'currentEditPlan',
      'getSelectedPlansCartInfo',
      'enableStandaloneCycleLimit',
      'getUserCurrentSubscriptions',
      'plansofBizBundle',
      'migratePlanState',
      'boughtPlans',
      'disableMonthly2AnnualTip',
      'pbxBaseInfo',
    ]),
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    cycleAndUsageFee: {
      get() {
        return [this.pbxBaseInfo.billingCycle, this.pbxBaseInfo.basePlanCode];
      },
      set(val) {
        const [billingCycle, usageFeePlanCode] = val
        this.setPbxBillingCycle(billingCycle);
        this.setPbxBasePlanCode(usageFeePlanCode);
      }
    },
    showCycle () {
      return true;
    },
    pbxBasePlans() {
      return computedPbxBasePlans(this.planMapFlat);
    },
    pbxBasePlanCycleMap() {
      return groupBy(this.pbxBasePlans, 'billing_cycle');
    },
    pbxRegions() {
      return regions.filter((x) => {
        if (x.key === PBX_REGIONS.JP) return false;
        if (this.$config('hideZoomPhoneUKIRPlans')) return x.key !== PBX_REGIONS.UK_AND_IRELAND;
        return true;
      });
    },
    annualAddonPlans() {
      const newLimit = this.enableStandaloneCycleLimit ? [planTypes.ZOOM_SCHEDULER, planTypes.ZOOM_WHITEBOARD] : [];
      return this.getUserCurrentSubscriptions.filter(item => (
        limitBillingCycleWithBaseZoneTypes.concat(newLimit).includes(item.type) &&
        item.billing_cycle === BILLING_CYCLE.ANNUAL
      ));
    },
    callingPlanOptions() {
      return this.pbxRegions.map((x) => ({
        id: x.key,
        name: this.$t(x.label),
        desc: '',
        min: this.minCallingPlan,
        max: this.maxCallingPlan,
      }));
    },
    maxCallingPlan() {
      return maxBizAndBizPlusLicenseCount;
    },
    costMap() {
      return costMap(this.planMapFlat, this.callingPlanRegions);
    },
    selectedZoBundlePlans() {
      return plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
    },
    boughtBizPlan() {
      return plansof(this.boughtPlans).category(BASE_PLAN_SUB_TYPES.BIZ).get(0);
    },
    cascadeCycleConfig() {
      const { payAsYouGo } = this.pbxBasePlanCycleMap;
      let cycleList = [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL]
      const nonStandardCycleZoBundlePlan = this.selectedZoBundlePlans.find(p => ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(p.billing_cycle))

      if (this.migratePlanState.is.biz2bundle && ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(this.boughtBizPlan?.billing_cycle)) {
        cycleList.push(this.boughtBizPlan.billing_cycle)
      } else if (nonStandardCycleZoBundlePlan) {
        cycleList.push(nonStandardCycleZoBundlePlan.billing_cycle)
      }
      if (cycleList.some(cycle => ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(cycle))) {
        // only show non-standard billing cycle and annual when includes non-standard billing cycle
        cycleList = cycleList.filter(cycle => {
          return cycle === nonStandardCycleZoBundlePlan.billing_cycle || cycle === BILLING_CYCLE.ANNUAL
        })
      }
      cycleList.sort((a, b) => BillingCycleEnum[a] - BillingCycleEnum[b])
      return {
        group0: {
          title: this.$t('billing.select_billing_cycle'),
          // action: this.switchBillingCycle,
        },
        group1: {
          slot: 'group1-title',
          // action: this.switchUsageFee,
        },
        options: cycleList.map((cycle) => {
          return {
            value: cycle,
            title: this.$t(`billing.billing_cycle.${cycle}`),
            subTitle: this.$money(this.costMap[cycle], { forceDiff: true }),
            disabled: this.getBillingCycleDisabledDesc(cycle),
            ...this.costMap,
            description: this.$t(`billing.common.per_billing_cycle_user.${cycle}`),
            showSave: BILLING_CYCLE.ANNUAL === cycle,
            options: [...payAsYouGo, ...this.pbxBasePlanCycleMap[cycle]].map((plan) => {
              return {
                value: plan.plan_code,
                title: getZPCallingFeesText(plan).title,
                subTitle: this.$money(plan.amount, { forceDiff: true }),
                description: getZPCallingFeesText(plan).unit,
              };
            }),
          };
        }),
      };
    },
    plansSummary() {
      const selectedZoBundlePlans = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
      const [billingCycle, usageFeePlanCode] = this.cycleAndUsageFee;
      const cycleOption = this.cascadeCycleConfig.options.find((x) => x.value === billingCycle);
      const usageFeeOption = cycleOption.options.find((x) => x.value === usageFeePlanCode);
      const callingPlanLabels = selectedZoBundlePlans.map((plan) => {
        const { parent_category, category } = plan;
        const pbxCategoryCode = category.toUpperCase().replace(parent_category.toUpperCase(), '');
        return `${this.$t(`billing.pbx.cart_name.${pbxCategoryCode}`)}  •  ${plan.cartInfo.selected_number} ${this.$t(
          'billing.planform.unit.user',
        )}  •  ${this.$money(plan.amount)}${this.$t(`billing.common.per_billing_cycle_user.${plan.billing_cycle}`)}`;
      });
      const usageFeeLabel = `${usageFeeOption.title}  •  ${this.$t('billing.pbx.calling_fee_name.pay_you_go.MC')}  •  ${usageFeeOption.subTitle}${usageFeeOption.description}`;
      return [...callingPlanLabels, usageFeeLabel].join('<br/>');
    },
    plansConfig() {
      const plans = plansof(this.planMapFlat).type(planTypes.ZOOM_ONE_BUNDLE);
      const zobundle = this.callingPlanRegions.map((x) => {
        const plan_code = plans.clone().region(x.id).cycle(this.pbxBaseInfo.billingCycle).get(0)?.plan_code;
        const number = x.number;
        return {
          plan_code,
          number,
        };
      });
      const payload = {
        [planTypes.ZOOM_ONE_BUNDLE]: zobundle,
        [planTypes.ZOOM_PHONE]: [
          {
            plan_code: this.pbxBaseInfo.basePlanCode,
            number: 1,
          },
        ],
      };
      return payload;
    },
    plansConfigJson() {
      return JSON.stringify(this.plansConfig);
    },
    planDetailLabel() {
      return this.$t('billing.plan_details');
    },
    showAddAnotherRegionCTA() {
      return this.callingPlanRegions.length !== this.callingPlanOptions.length;
    },
    licenseTips() {
      return '';
    },
    planBillInfo() {
      return false;
    },
    trackingPlanName() {
      return `zoom one business plus`;
    },
    unlockBizLicenseCount() {
      return unlockBizLicenseCount;
    },
    hasBoughtJPPbxPlans(){
      return !!computedPbxPlans(this.boughtPlans)?.filter?.(item => item.region === PBX_REGIONS.JP)?.length
    }
  },
  watch: {
    plansConfigJson(nv, ov) {
      if (!this.ready) return;
      const [olds, news] = [ov, nv].map((json) => {
        const plansConfig = JSON.parse(json);
        return [].concat(...Object.values(plansConfig));
      });
      // TODO async check, may need to revert back to previous state
      this.updateSelectPlans(olds.map((x) => ({ plan_code: x.plan_code })));
      this.updateSelectPlans(news.map((x) => ({ plan_code: x.plan_code, cartInfo: { selected_number: x.number } })));
    },
    getSelectedPlans: {
      immediate: true,
      handler() {
        // if add-to-cart & purchased one region plan or only have pbx base plan
        if (this.$context.is.add2cart &&
          ((this.plansofBizBundle.pbxBasePlan && !this.plansofBizBundle.boughtBizPlusPlan.length) || this.plansofBizBundle.boughtBizPlusPlan.length > 0)) return;
        const { message } = checkBizPlusLicense();
        this.licenseErrTips = message;
      },
    },
  },
  methods: {
    i18n_cycle,
    ...mapActions(['switchSelectPlanAction']),
    ...mapMutations(['updateSelectPlans', 'setPbxBasePlanCode', 'setPbxBillingCycle']),
    async handlePlanDetailsClick() {
      this.$tracking.buyInteraction('plan details selected', this.trackingPlanName);
      await this.planDetailsDialog.confirm({ planCategory: 'bizplus' });
      await this.showFullComparisonDialog();
    },
    async handleExploreOtherPlansClick() {
      this.$tracking.buyInteraction('explore other plans', `${this.trackingPlanName} - explore other plans`);
      await this.showFullComparisonDialog();
    },
    async showFullComparisonDialog() {
      this.$tracking.buyInteraction('product details', 'full plan comparison - impression');
      await this.fullComparisonDialog.confirm();
    },
    addAnotherRegion() {
      this.$refs.regionsSelect.newItem();
    },
    getBillingCycleDisabledDesc(billingCycle) {
      const activeCycle = this.cycleAndUsageFee[0];
      if(!activeCycle) return false;
      if (activeCycle === billingCycle && billingCycle === BILLING_CYCLE.ANNUAL) {
        return false;
      }
      const exceedAddonPlans = this.getExceedCycleAddonPlans(BillingCycleEnum[billingCycle]);
      const planGroupByType = groupBy(exceedAddonPlans, 'type');
      const typeNames = Object.keys(planGroupByType).map(type => {
        const typeNameKey = get(planGroupByType[type], '0.typeNameKey', '');
        return this.$t(typeNameKey);
      }).filter(Boolean);
      if (exceedAddonPlans.length) {
        if (billingCycle === BILLING_CYCLE.MONTHLY) {
          return this.$t('billing.planform.monthly_base_disable_tooltip', [typeNames.join(','), this.$t('billing.planform.title.zoom_meeting_zoomonenew')]);
        } else {
          return true;
        }
      }
      if (this.disableMonthly2AnnualTip && billingCycle === BILLING_CYCLE.ANNUAL) {
        return this.disableMonthly2AnnualTip;
      }
      return false;
    },
    initCallPlanRegions() {
      const zobundlePlans = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
      this.callingPlanRegions = zobundlePlans.map((x) => {
        const { disabled, tooltip, shouldDisabled, disableDelete, disableSwitch, deleteTips } = this.getCallingPlanDisable(x);
        return {
          id: x.region,
          number: x.cartInfo?.selected_number,
          plan_code: x.plan_code,
          disabled: disabled,
          inputTooltip: tooltip,
          shouldDisabled,
          disableSwitch,
          disableDelete,
          deleteTips
        }
      });
    },
    init() {
      const zobundlePlans = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
      const pbxBasePlan = plansof(this.getSelectedPlans).type(planTypes.ZOOM_PHONE).get(0);
      this.initCallPlanRegions();
      this.cycleAndUsageFee = [zobundlePlans?.[0]?.billing_cycle || BILLING_CYCLE.MONTHLY, pbxBasePlan.plan_code];
      this.$nextTick(() => {
        this.ready = true;
      });
    },
    getCallingPlanDisable() {
      return {
        disabled: false,
        tooltip: ''
      }
    },
    getExceedCycleAddonPlans(cycleValue) {
      const newLimit = this.enableStandaloneCycleLimit ? [planTypes.ZOOM_SCHEDULER, planTypes.ZOOM_WHITEBOARD] : [];
      return this.getUserCurrentSubscriptions.filter(item => (
        limitBillingCycleWithBaseZoneTypes.concat(newLimit).includes(item.type) &&
        item.cycleValue > cycleValue
      ));
    },
  },
  created() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.opc-zobundle {
  &__main {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #dfdfe4;
  }

  .reason-tips {
    margin-top: 12px;

    .zm-alert__description {
      display: flex;
      align-items: center;
    }

    p {
      margin-left: 13px;
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 150%;
      color: #515079;
    }
  }

  .zm-alert--warning{
    color: #00053D !important;
    background: #FEF7D9 !important;
    margin-bottom: 24px;
  }
}
</style>
<style lang="scss">
@import '@/styles/responsive.scss';
.opc-zm-base-card__name{
  @include media(mobile,tablet){
    display: block;
    font-size: 16px;
    width: 50vw;
  }
}

.opc-card-cascade .opc-card-cascade__item .opc-card-radio  {
  @include media(mobile,tablet){
    flex-direction: column;
  }
}

.opc-foldable.is-active {
  .opc-zm-base-card__name {
    @include media(mobile){
      width: 55vw;
    }
  }
}

.zm-message.zoom-one-boundle__auto-add {
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0px 8px 24px 0px rgba(26, 55, 96, 0.16);
  display: flex;
  padding: 16px 12px;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;

  .zm-icon-info {
    color: #0b5cff;
  }

  .zm-message__content {
    padding-right: 40px !important;
  }
}
</style>
